/**
 * How thread attachments are split between the chat and the composer.
 *
 * A file has no backend link to the message it was sent with, so the link is
 * derived: a file belongs to the first message *its own uploader* posted at or
 * after the upload — the "send" that moves it out of the composer into that
 * bubble. Derived from timestamps, so it survives a reload without stored state.
 *
 * Pulled out of the component because this is the subtlest logic in the feature
 * and it is worth testing on its own — in particular that the composer strip is
 * per-user: `attachedFiles` holds every FILE item on the thread, from everyone.
 */

/** Just the fields of a thread entry this derivation reads. */
export interface AttachmentEntry {
    id: string;
    type: string;
    source: { id?: string | null };
    timestamp?: string | null;
}

/** A FILE context item — the flat string map is all that is needed here. */
export interface AttachmentItem {
    data: Record<string, string>;
}

/** A file projected for the timeline (what a message bubble renders). */
export interface TimelineFile {
    fileId: string;
    filename: string;
    size?: string;
}

const millis = (value?: string | null): number =>
    value ? new Date(value).getTime() : 0;

export interface DerivedThreadFiles {
    /** Sent files, grouped under the id of the message they were sent with. */
    filesByEntryId: Record<string, TimelineFile[]>;
    /** The current user's own not-yet-sent uploads (the composer strip). */
    unsentFileIds: Set<string>;
}

/**
 * Split a thread's files into sent (grouped by message) and the current user's
 * unsent uploads. A file that is unsent but was uploaded by *someone else* lands
 * in neither: it is theirs to send or clear, not mine to see in my composer.
 */
export function deriveThreadFiles(
    entries: readonly AttachmentEntry[],
    files: readonly AttachmentItem[],
    userId: string | null | undefined
): DerivedThreadFiles {
    const contributions = entries
        .filter((e) => e.type === "contribution")
        .slice()
        .sort((a, b) => millis(a.timestamp) - millis(b.timestamp));

    const messageIdFor = (file: AttachmentItem): string | null => {
        const uploadedAt = millis(file.data.uploaded_at);
        const by = file.data.uploaded_by_id;
        const target = contributions.find(
            (e) => e.source.id === by && millis(e.timestamp) >= uploadedAt
        );
        return target?.id ?? null;
    };

    const filesByEntryId: Record<string, TimelineFile[]> = {};
    const unsentFileIds = new Set<string>();
    for (const f of files) {
        const entryId = messageIdFor(f);
        if (entryId) {
            (filesByEntryId[entryId] ??= []).push({
                fileId: f.data.file_id,
                filename: f.data.filename,
                size: f.data.size,
            });
        } else if (userId && f.data.uploaded_by_id === userId) {
            unsentFileIds.add(f.data.file_id);
        }
    }
    return { filesByEntryId, unsentFileIds };
}
